'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CommissionCalculator() {
  const t = useTranslations("tools.commissionCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Tab state
  const [activeTab, setActiveTab] = useState<'simple' | 'tiered'>('simple');

  // Simple calculator state
  const [simpleInputs, setSimpleInputs] = useState({
    salesPrice: '',
    commissionRate: ''
  });
  const [simpleResults, setSimpleResults] = useState<{
    commission: number;
    netAmount: number;
  } | null>(null);

  // Tiered calculator state
  const [tieredInputs, setTieredInputs] = useState({
    salesPrice: '',
    hasBase: false,
    baseCommission: '',
    baseCommissionUnit: 'dollar' as 'dollar' | 'percent',
    isTiered: false,
    fixedCommission: '',
    fixedCommissionUnit: 'percent' as 'percent' | 'dollar',
    tiers: [
      { from: 0, to: '', commission: '', unit: 'percent' as 'percent' | 'dollar' },
      { from: 0, to: '', commission: '', unit: 'percent' as 'percent' | 'dollar' },
      { from: 0, to: '', commission: '', unit: 'percent' as 'percent' | 'dollar' }
    ]
  });
  const [tieredResults, setTieredResults] = useState<{
    totalCommission: number;
    breakdown: Array<{ label: string; value: number }>;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '3rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    tabsContainer: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginBottom: '2rem', justifyContent: 'center', flexWrap: 'wrap' as const },
      {},
      {},
      {}
    ),
    tabBtn: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        background: 'white',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        fontWeight: 600,
        color: '#64748b',
        transition: 'all 0.3s ease'
      },
      { padding: '0.75rem 1.5rem', fontSize: '0.95rem' },
      { padding: '0.75rem 1.5rem', fontSize: '0.95rem' },
      { padding: '0.75rem 1.5rem', fontSize: '0.95rem' }
    ),
    tabBtnActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderColor: 'transparent'
    },
    calculatorCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGroup: getResponsiveStyle(
      { marginBottom: '1.5rem' },
      {},
      {},
      {}
    ),
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 600 },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' },
      { fontSize: '0.95rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' },
      { padding: '0.75rem 1rem' }
    ),
    helpText: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginTop: '0.25rem',
      fontStyle: 'italic' as const
    },
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginTop: '0.5rem'
    },
    radioOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer'
    },
    inputWithUnit: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center'
    },
    unitSelect: {
      width: '80px',
      flexShrink: 0
    },
    tierTable: getResponsiveStyle(
      {
        width: '100%',
        marginTop: '1rem',
        borderCollapse: 'collapse' as const
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    tierTableTh: getResponsiveStyle(
      {
        background: '#f1f5f9',
        textAlign: 'left' as const,
        fontWeight: 600,
        color: '#475569'
      },
      { padding: '0.5rem 0.25rem', fontSize: '0.85rem' },
      { padding: '0.75rem', fontSize: '0.9rem' },
      { padding: '0.75rem', fontSize: '0.9rem' }
    ),
    tierTableTd: getResponsiveStyle(
      {
        borderBottom: '1px solid #e2e8f0'
      },
      { padding: '0.5rem 0.25rem' },
      { padding: '0.75rem' },
      { padding: '0.75rem' }
    ),
    tierInput: {
      width: '100%',
      padding: '0.5rem',
      border: '1px solid #e2e8f0',
      borderRadius: '0.375rem',
      fontSize: '0.9rem'
    },
    tierSelect: {
      width: '70px',
      padding: '0.5rem',
      border: '1px solid #e2e8f0',
      borderRadius: '0.375rem',
      fontSize: '0.9rem'
    },
    tierFrom: {
      color: '#64748b',
      fontWeight: 500
    },
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem'
      },
      { padding: '1rem', fontSize: '1.1rem' },
      { padding: '1rem', fontSize: '1.1rem' },
      { padding: '1rem', fontSize: '1.1rem' }
    ),
    clearBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'white',
        color: '#64748b',
        border: '2px solid #e2e8f0',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '0.5rem'
      },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' },
      { padding: '0.75rem', fontSize: '1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        borderRadius: '1rem',
        marginTop: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: '#065f46', marginBottom: '1.5rem', textAlign: 'center' as const },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    resultLabel: {
      color: '#475569',
      fontWeight: 600,
      fontSize: '1rem'
    },
    resultValue: getResponsiveStyle(
      { color: '#059669', fontWeight: 700 },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    ),
    resultBreakdown: {
      background: 'white',
      borderRadius: '0.5rem',
      padding: '1rem',
      marginTop: '1rem'
    },
    breakdownTitle: {
      color: '#065f46',
      fontWeight: 600,
      marginBottom: '0.75rem',
      fontSize: '1rem'
    },
    breakdownItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.5rem 0',
      borderBottom: '1px solid #e2e8f0',
      fontSize: '0.9rem'
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.8, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.2rem' },
      { fontSize: '1.2rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const
    }
  };

  const handleSimpleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const salesPrice = parseFloat(simpleInputs.salesPrice);
    const commissionRate = parseFloat(simpleInputs.commissionRate);

    if (isNaN(salesPrice) || isNaN(commissionRate)) return;

    const commission = salesPrice * (commissionRate / 100);
    const netAmount = salesPrice - commission;

    setSimpleResults({ commission, netAmount });
  };

  const handleSimpleClear = () => {
    setSimpleInputs({ salesPrice: '', commissionRate: '' });
    setSimpleResults(null);
  };

  const handleTieredCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const salesPrice = parseFloat(tieredInputs.salesPrice);
    if (isNaN(salesPrice)) return;

    let totalCommission = 0;
    const breakdown: Array<{ label: string; value: number }> = [];

    // Base commission
    if (tieredInputs.hasBase) {
      const baseValue = parseFloat(tieredInputs.baseCommission) || 0;
      const baseCommission = tieredInputs.baseCommissionUnit === 'percent'
        ? salesPrice * (baseValue / 100)
        : baseValue;
      totalCommission += baseCommission;
      breakdown.push({ label: t('baseCommission'), value: baseCommission });
    }

    // Tiered or fixed commission
    if (tieredInputs.isTiered) {
      let remainingAmount = salesPrice;
      let previousTo = 0;

      tieredInputs.tiers.forEach((tier, index) => {
        const toValue = tier.to ? parseFloat(tier.to) : Infinity;
        const commissionValue = parseFloat(tier.commission) || 0;

        if (commissionValue > 0 && remainingAmount > 0) {
          const tierAmount = Math.min(toValue - previousTo, remainingAmount);

          if (tierAmount > 0) {
            const tierCommission = tier.unit === 'percent'
              ? tierAmount * (commissionValue / 100)
              : commissionValue;
            totalCommission += tierCommission;

            const fromFormatted = '$' + previousTo.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const toFormatted = toValue === Infinity ? '∞' : '$' + toValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            breakdown.push({
              label: `${t('tier')} ${index + 1} (${fromFormatted} - ${toFormatted})`,
              value: tierCommission
            });

            remainingAmount -= tierAmount;
            previousTo = toValue;
          }
        }
      });
    } else {
      const fixedValue = parseFloat(tieredInputs.fixedCommission) || 0;
      const fixedCommission = tieredInputs.fixedCommissionUnit === 'percent'
        ? salesPrice * (fixedValue / 100)
        : fixedValue;
      totalCommission += fixedCommission;
      breakdown.push({ label: t('commission'), value: fixedCommission });
    }

    setTieredResults({ totalCommission, breakdown });
  };

  const handleTieredClear = () => {
    setTieredInputs({
      salesPrice: '',
      hasBase: false,
      baseCommission: '',
      baseCommissionUnit: 'dollar',
      isTiered: false,
      fixedCommission: '',
      fixedCommissionUnit: 'percent',
      tiers: [
        { from: 0, to: '', commission: '', unit: 'percent' },
        { from: 0, to: '', commission: '', unit: 'percent' },
        { from: 0, to: '', commission: '', unit: 'percent' }
      ]
    });
    setTieredResults(null);
  };

  const updateTierFrom = (index: number) => {
    if (index === 0) return 0;
    const prevTo = tieredInputs.tiers[index - 1].to;
    return prevTo ? parseFloat(prevTo) : 0;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t("toolTitle")}</h2>
          </div>

          <div style={styles.tabsContainer}>
            <button
              style={{
                ...styles.tabBtn,
                ...(activeTab === 'simple' ? styles.tabBtnActive : {})
              }}
              onClick={() => setActiveTab('simple')}
            >
              {t("simpleTab")}
            </button>
            <button
              style={{
                ...styles.tabBtn,
                ...(activeTab === 'tiered' ? styles.tabBtnActive : {})
              }}
              onClick={() => setActiveTab('tiered')}
            >
              {t("tieredTab")}
            </button>
          </div>

          <div style={styles.calculatorCard}>
            {/* Simple Calculator */}
            {activeTab === 'simple' && (
              <div>
                <form onSubmit={handleSimpleCalculate}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="sales-price">
                      {t("salesPrice")}
                    </label>
                    <input
                      type="number"
                      id="sales-price"
                      step="0.01"
                      min="0"
                      placeholder="200000"
                      value={simpleInputs.salesPrice}
                      onChange={(e) => setSimpleInputs({ ...simpleInputs, salesPrice: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="commission-rate">
                      {t("commissionRate")}
                    </label>
                    <input
                      type="number"
                      id="commission-rate"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="3"
                      value={simpleInputs.commissionRate}
                      onChange={(e) => setSimpleInputs({ ...simpleInputs, commissionRate: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>

                  <button type="submit" style={styles.calculateBtn}>
                    {t("calculateButton")}
                  </button>
                  <button type="button" style={styles.clearBtn} onClick={handleSimpleClear}>
                    {t("clearButton")}
                  </button>
                </form>

                {simpleResults && (
                  <div style={styles.results}>
                    <h3 style={styles.resultsTitle}>{t("resultsTitle")}</h3>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t("totalCommission")}</span>
                      <span style={styles.resultValue}>
                        ${simpleResults.commission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t("netAmount")}</span>
                      <span style={styles.resultValue}>
                        ${simpleResults.netAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tiered Calculator */}
            {activeTab === 'tiered' && (
              <div>
                <form onSubmit={handleTieredCalculate}>
                  <div style={styles.formGroup}>
                    <label style={styles.label} htmlFor="tiered-sales-price">
                      {t("salesPrice")}
                    </label>
                    <input
                      type="number"
                      id="tiered-sales-price"
                      step="0.01"
                      min="0"
                      placeholder="200000"
                      value={tieredInputs.salesPrice}
                      onChange={(e) => setTieredInputs({ ...tieredInputs, salesPrice: e.target.value })}
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("hasBaseQuestion")}</label>
                    <p style={styles.helpText}>{t("hasBaseHelp")}</p>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="has-base"
                          checked={!tieredInputs.hasBase}
                          onChange={() => setTieredInputs({ ...tieredInputs, hasBase: false })}
                        />
                        <span>{t("no")}</span>
                      </label>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="has-base"
                          checked={tieredInputs.hasBase}
                          onChange={() => setTieredInputs({ ...tieredInputs, hasBase: true })}
                        />
                        <span>{t("yes")}</span>
                      </label>
                    </div>
                  </div>

                  {tieredInputs.hasBase && (
                    <div style={styles.formGroup}>
                      <label style={styles.label} htmlFor="base-commission">
                        {t("baseCommissionLabel")}
                      </label>
                      <div style={styles.inputWithUnit}>
                        <input
                          type="number"
                          id="base-commission"
                          step="0.01"
                          min="0"
                          placeholder="1000"
                          value={tieredInputs.baseCommission}
                          onChange={(e) => setTieredInputs({ ...tieredInputs, baseCommission: e.target.value })}
                          style={{ ...styles.input, flex: 1 }}
                        />
                        <select
                          value={tieredInputs.baseCommissionUnit}
                          onChange={(e) => setTieredInputs({ ...tieredInputs, baseCommissionUnit: e.target.value as 'dollar' | 'percent' })}
                          style={{ ...styles.select, ...styles.unitSelect }}
                        >
                          <option value="dollar">$</option>
                          <option value="percent">%</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t("isTieredQuestion")}</label>
                    <p style={styles.helpText}>{t("isTieredHelp")}</p>
                    <div style={styles.radioGroup}>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="is-tiered"
                          checked={!tieredInputs.isTiered}
                          onChange={() => setTieredInputs({ ...tieredInputs, isTiered: false })}
                        />
                        <span>{t("no")}</span>
                      </label>
                      <label style={styles.radioOption}>
                        <input
                          type="radio"
                          name="is-tiered"
                          checked={tieredInputs.isTiered}
                          onChange={() => setTieredInputs({ ...tieredInputs, isTiered: true })}
                        />
                        <span>{t("yes")}</span>
                      </label>
                    </div>
                  </div>

                  {!tieredInputs.isTiered && (
                    <div style={styles.formGroup}>
                      <label style={styles.label} htmlFor="fixed-commission">
                        {t("commissionLabel")}
                      </label>
                      <div style={styles.inputWithUnit}>
                        <input
                          type="number"
                          id="fixed-commission"
                          step="0.01"
                          min="0"
                          placeholder="3"
                          value={tieredInputs.fixedCommission}
                          onChange={(e) => setTieredInputs({ ...tieredInputs, fixedCommission: e.target.value })}
                          style={{ ...styles.input, flex: 1 }}
                        />
                        <select
                          value={tieredInputs.fixedCommissionUnit}
                          onChange={(e) => setTieredInputs({ ...tieredInputs, fixedCommissionUnit: e.target.value as 'percent' | 'dollar' })}
                          style={{ ...styles.select, ...styles.unitSelect }}
                        >
                          <option value="percent">%</option>
                          <option value="dollar">$</option>
                        </select>
                      </div>
                    </div>
                  )}

                  {tieredInputs.isTiered && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>{t("tieredStructureLabel")}</label>
                      <p style={styles.helpText}>{t("tieredStructureHelp")}</p>
                      <table style={styles.tierTable}>
                        <thead>
                          <tr>
                            <th style={styles.tierTableTh}>{t("from")}</th>
                            <th style={styles.tierTableTh}>{t("to")}</th>
                            <th style={styles.tierTableTh}>{t("commission")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tieredInputs.tiers.map((tier, index) => (
                            <tr key={index}>
                              <td style={styles.tierTableTd}>
                                <span style={styles.tierFrom}>
                                  {index === 0 ? '$0' : tier.to ? `$${updateTierFrom(index).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                </span>
                              </td>
                              <td style={styles.tierTableTd}>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder={index === 0 ? "20000" : ""}
                                  value={tier.to}
                                  onChange={(e) => {
                                    const newTiers = [...tieredInputs.tiers];
                                    newTiers[index].to = e.target.value;
                                    if (index < newTiers.length - 1) {
                                      newTiers[index + 1].from = e.target.value ? parseFloat(e.target.value) : 0;
                                    }
                                    setTieredInputs({ ...tieredInputs, tiers: newTiers });
                                  }}
                                  style={styles.tierInput}
                                />
                              </td>
                              <td style={styles.tierTableTd}>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder={index === 0 ? "3" : index === 1 ? "5" : ""}
                                    value={tier.commission}
                                    onChange={(e) => {
                                      const newTiers = [...tieredInputs.tiers];
                                      newTiers[index].commission = e.target.value;
                                      setTieredInputs({ ...tieredInputs, tiers: newTiers });
                                    }}
                                    style={{ ...styles.tierInput, width: '60%' }}
                                  />
                                  <select
                                    value={tier.unit}
                                    onChange={(e) => {
                                      const newTiers = [...tieredInputs.tiers];
                                      newTiers[index].unit = e.target.value as 'percent' | 'dollar';
                                      setTieredInputs({ ...tieredInputs, tiers: newTiers });
                                    }}
                                    style={{ ...styles.tierSelect, width: '35%' }}
                                  >
                                    <option value="percent">%</option>
                                    <option value="dollar">$</option>
                                  </select>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <button type="submit" style={styles.calculateBtn}>
                    {t("calculateButton")}
                  </button>
                  <button type="button" style={styles.clearBtn} onClick={handleTieredClear}>
                    {t("clearButton")}
                  </button>
                </form>

                {tieredResults && (
                  <div style={styles.results}>
                    <h3 style={styles.resultsTitle}>{t("resultsTitle")}</h3>
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t("totalCommission")}</span>
                      <span style={styles.resultValue}>
                        ${tieredResults.totalCommission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div style={styles.resultBreakdown}>
                      <div style={styles.breakdownTitle}>{t("breakdownTitle")}</div>
                      {tieredResults.breakdown.map((item, index) => (
                        <div key={index} style={{ ...styles.breakdownItem, ...(index === tieredResults.breakdown.length - 1 ? { borderBottom: 'none' } : {}) }}>
                          <span>{item.label}</span>
                          <span style={{ fontWeight: 600, color: '#059669' }}>
                            ${item.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("howToStep1")}</li>
          <li style={styles.listItem}>{t("howToStep2")}</li>
          <li style={styles.listItem}>{t("howToStep3")}</li>
          <li style={styles.listItem}>{t("howToStep4")}</li>
          <li style={styles.listItem}>{t("howToStep5")}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <p style={styles.paragraph}>{t("insightsParagraph1")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t("insightAccuracy")}</strong> {t("insightAccuracyText")}</li>
          <li style={styles.listItem}><strong>{t("insightTimeSaving")}</strong> {t("insightTimeSavingText")}</li>
          <li style={styles.listItem}><strong>{t("insightTransparency")}</strong> {t("insightTransparencyText")}</li>
          <li style={styles.listItem}><strong>{t("insightPlanning")}</strong> {t("insightPlanningText")}</li>
          <li style={styles.listItem}><strong>{t("insightScalability")}</strong> {t("insightScalabilityText")}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>{t("detailsCommissionTypesTitle")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><strong>{t("detailsSimpleRate")}</strong> {t("detailsSimpleRateText")}</li>
          <li style={styles.listItem}><strong>{t("detailsTieredCommission")}</strong> {t("detailsTieredCommissionText")}</li>
          <li style={styles.listItem}><strong>{t("detailsBaseCommission")}</strong> {t("detailsBaseCommissionText")}</li>
          <li style={styles.listItem}><strong>{t("detailsSplitDeals")}</strong> {t("detailsSplitDealsText")}</li>
        </ul>

        <h3 style={{ ...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t("detailsBestPracticesTitle")}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("detailsBestPractice1")}</li>
          <li style={styles.listItem}>{t("detailsBestPractice2")}</li>
          <li style={styles.listItem}>{t("detailsBestPractice3")}</li>
          <li style={styles.listItem}>{t("detailsBestPractice4")}</li>
          <li style={styles.listItem}>{t("detailsBestPractice5")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq5Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq6Question")}</h3>
          <p style={styles.faqAnswer}>{t("faq6Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a href="https://www.qobra.co/glossary/commission-calculator" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t("reference1")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.calculator.net/commission-calculator.html" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t("reference2")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.omnicalculator.com/finance/commission" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
              {t("reference3")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}